import { access } from '@driftt/traffic-control'

export const {
  hasScopes,
  hasAnyScope,
  hasGates,
  isAdmin,
  isBot,
  isAgent,
  isUser,
  hasAnyRole,
  hasFeatures,
  hasAnyDefaultRole,
  isRbacUser,
} = access

export const createHasGate =
  (...gates: string[]) =>
  (): boolean =>
    access.hasGates(...gates)

export const hasLegacyRoutingReport = createHasGate(
  'enable_legacy_routing_report'
)

export const userHasAllReportAccess = () => {
  return isAdmin() || hasAnyRole('Reporting')
}

export const hasFeatureBetaGate = createHasGate('reports_feature_beta')

export const hasCASIMGate = createHasGate('reports_feature_casim')
export const hasImpressionSendMetrics = createHasGate(
  'has_aggregated_playbook_sends'
)
export const hasExpandedPlaybookMetrics = createHasGate(
  'has_expanded_playbook_metrics'
)

export const hasInsightsYearHistory = () =>
  access.hasGates('premium_enterprise', 'enterprise_04_2018')
export const hasProOrAboveAccess = () =>
  access.hasGates('premium_enterprise') || access.hasGates('premium_team')

export const hasPlaybookExperiments = () =>
  access.hasGates('has_playbook_variation_testing')

const hasConvoAnalysisSunset = createHasGate('has_sunset_convo_analysis')

export const orgHasShowConvoAnalysis = () =>
  hasGates('has_insights') && !hasConvoAnalysisSunset()
export const hasSavedSearches = createHasGate('has_saved_searches')
export const hasInsightsBeta = createHasGate('insights_beta')
export const hasClusterInsights = createHasGate('has_cluster_insights')
export const hasInsightsConversationsChart = createHasGate(
  'has_insights_conversations_chart'
)

const canAccessFastlaneWithGate = () =>
  (userHasAllReportAccess() || userHasPlaybooksRole()) &&
  hasGates('has_form_submission_fast_lane')

const userHasSalesResultsReportsFeature = () =>
  hasFeatures({ featureName: 'sales_results_reports', accessLevel: 'READ' })
const userHasChatPlaybookReportsFeature = () =>
  hasFeatures({ featureName: 'chat_playbook_reports', accessLevel: 'READ' })
const userHasFastlaneReportsReadFeature = () =>
  hasFeatures({
    featureName: 'fastlane_playbook_reports',
    accessLevel: 'READ',
  })
const userHasMyConversationReportsFeature = () =>
  hasFeatures({ featureName: 'my_conversations_reports', accessLevel: 'READ' })
const userHasOrgConversationReportsFeature = () =>
  hasFeatures({ featureName: 'org_conversations_reports', accessLevel: 'READ' })
const userHasTeamPerformanceReportsFeature = () =>
  hasFeatures({ featureName: 'team_performance_reports', accessLevel: 'READ' })
export const userHasSalesUsageReportsFeature = () =>
  isRbacUser() &&
  hasFeatures({ featureName: 'team_performance_reports', accessLevel: 'READ' }) // limited to FY'22 plans but still using the team_performance_reports feature
export const userHasTeamUsageDashboardFeature = () =>
  isRbacUser() &&
  hasFeatures({ featureName: 'team_usage_dashboard', accessLevel: 'READ' })
const userHasRoutingReportsFeature = () =>
  hasFeatures({ featureName: 'routing_reports', accessLevel: 'READ' })
const userHasAllConvoViewFeature = () =>
  hasFeatures({ featureName: 'convo_view_all', accessLevel: 'READ' })
const userHasChatPlaybookWriteFeature = () =>
  hasFeatures({ featureName: 'chat_playbooks', accessLevel: 'WRITE' })
const userHasFastlanePlaybooksWriteFeature = () =>
  hasFeatures({
    featureName: 'fastlane_playbooks',
    accessLevel: 'WRITE',
  })
const userHasEmailPlaybookWriteFeature = () =>
  hasFeatures({ featureName: 'email_playbooks', accessLevel: 'WRITE' })
const userHasConvoAnalysisFeature = () =>
  hasFeatures({ featureName: 'conversation_analysis', accessLevel: 'READ' })
const userHasConvoAnalysisWriteFeature = () =>
  hasFeatures({ featureName: 'conversation_analysis', accessLevel: 'WRITE' })
const userHasAudiencesReadFeature = () =>
  hasFeatures({ featureName: 'audiences', accessLevel: 'READ' })
export const userHasServiceReports = () =>
  hasFeatures({ featureName: 'service_reports', accessLevel: 'READ' })
export const userHasCSATReports = createHasGate('service_csat_reports')

export const isNonAdminChatUser = () => hasAnyScope('member') && !isAdmin()
export const userHasPlaybooksRole = () => hasAnyRole('Playbooks')
export const userHasMarketerRole = () => hasAnyDefaultRole('Marketer')

export const userHasEmailPlaybooksAccess = () => hasGates('send_email_skill')
export const userHasChatPlaybookAccess = () =>
  userHasAllReportAccess() ||
  userHasPlaybooksRole() ||
  userHasChatPlaybookReportsFeature()

export const userHasSiteCoverageAccess = userHasChatPlaybookAccess

export const userHasSalesResultsAccess = () =>
  userHasAllReportAccess() ||
  userHasPlaybooksRole() ||
  userHasSalesResultsReportsFeature()

export const userHasFastlanePlaybookAccess = () =>
  canAccessFastlaneWithGate() || userHasFastlaneReportsReadFeature()

export const userWillGetFastlaneAlertInMeetingsReport = () =>
  userHasFastlanePlaybookAccess() && (isAdmin() || userHasMarketerRole())

export const hasPlaybookReportAccess = () =>
  userHasChatPlaybookAccess() ||
  userHasFastlanePlaybookAccess() ||
  userHasEmailPlaybooksAccess()

export const getReadableWorkspacesWithPlaybookReportPermissions = (
  isFastlane: boolean
): string[] => {
  const workspaceIds = access.getWorkspacesWithPermission(
    isFastlane ? 'fastlane_playbook_reports' : 'chat_playbook_reports',
    'READ'
  )
  if (workspaceIds.length === 0 && isFastlane && canAccessFastlaneWithGate()) {
    // CBO-300 Workaround where org has the fastlane gate
    // but is not part of the plan with the fastlane report feature
    return ['default']
  }
  return workspaceIds
}

export const userHasConversationReportsAccess = () =>
  userHasAllReportAccess() ||
  isNonAdminChatUser() ||
  userHasMyConversationReportsFeature() ||
  userHasOrgConversationReportsFeature()

export const userHasContactsAccess = () =>
  isAdmin() ||
  hasScopes('member') ||
  hasFeatures({
    featureName: 'contacts',
    accessLevel: 'READ',
  })

export const userHasTeamPerformanceAccess = () =>
  userHasAllReportAccess() || userHasTeamPerformanceReportsFeature()
export const userHasRoutingAccess = () =>
  userHasAllReportAccess() || userHasRoutingReportsFeature()
export const userHasAllConvoViewAccess = () =>
  isAdmin() || userHasAllConvoViewFeature()

export const userHasConvoAnalysis = () =>
  isAdmin() || hasAnyRole('Reporting') || userHasConvoAnalysisFeature()

export const showConvoAnalysisInReports = () =>
  (orgHasShowConvoAnalysis() && (userHasConvoAnalysisFeature() || isAdmin())) ||
  isBot()

export const userCanReadChatPlaybooks = () =>
  isAdmin() ||
  userHasPlaybooksRole() ||
  hasFeatures({ featureName: 'chat_playbooks', accessLevel: 'READ' })

export const userCanEditBotPlaybooks = () =>
  isAdmin() ||
  userHasPlaybooksRole() ||
  userHasChatPlaybookWriteFeature() ||
  userHasFastlanePlaybooksWriteFeature()
export const userCanEditEmailPlaybooks = () =>
  isAdmin() || userHasPlaybooksRole() || userHasEmailPlaybookWriteFeature()

export const getPlanName = (): string => {
  if (access.hasGates('enterprise_04_2018')) {
    return 'Enterprise'
  }
  if (access.hasGates('premium_enterprise')) {
    return 'Premium'
  }
  if (access.hasGates('premium_team')) {
    return 'Pro'
  }
  if (access.hasGates('premium_drift')) {
    return 'Standard'
  }
  return 'Free'
}
export const userHasSavedSearchWrite = () =>
  isAdmin() || userHasConvoAnalysisWriteFeature()
export const userHasOrgIntegrationAccess = () =>
  isAdmin() ||
  hasFeatures({ featureName: 'org_integrations', accessLevel: 'READ' })
export const userHasOrgConversationReports = () =>
  userHasAllReportAccess() || userHasOrgConversationReportsFeature()
export const isEmailOnlyOrg = () => hasGates('has_siftrock_bots_v2')

const isEmailOrg = () =>
  hasGates('has_siftrock_bots_v2') ||
  hasGates('has_siftrock_bots_v2_existing_org')

export const hasDemoOrgGate = createHasGate('has_demo_org')
export const hasRealTimeDashboard = () =>
  (access.isRbacUser() || access.hasAnyScope('org_admin')) &&
  hasFeatures({ featureName: 'has_real_time_dashboard', accessLevel: 'READ' })
export const hasRealTimeDashboardMissedChatsGate = () => hasRealTimeDashboard()
export const hasConversationRatingDropGate = createHasGate(
  'has_conversation_rating_drop'
)
export const hasResponseTimerGate = createHasGate('has_convo_response_timer')
export const hasResponseTimerReportingGate = createHasGate(
  'has_convo_response_timer_reporting'
)
export const hasCallsReportOrgAccess = () => hasGates('has_calls_reporting')
export const hasDriftVoice = () =>
  userHasConversationReportsAccess() &&
  hasGates('has_drift_voice') &&
  hasCallsReportOrgAccess()
export const hasAudiences = () => createHasGate('AUDIENCE')

export const userHasAudiencesAccess = () =>
  userHasAudiencesReadFeature() ||
  (!isEmailOnlyOrg() &&
    hasAudiences() &&
    (userHasAllReportAccess() || userHasPlaybooksRole()))

const userHasEmailCampaignAccessLegacy = () =>
  !access.isRbacUser() && isEmailOrg()
const userHasEmailCampaignReportAccessRbac = () =>
  access.isRbacUser() &&
  hasFeatures({ featureName: 'email_campaign_reports', accessLevel: 'READ' })
const userCanEditEmailCampaignsRbac = () =>
  access.isRbacUser() &&
  hasFeatures({ featureName: 'email_campaigns', accessLevel: 'WRITE' })

export const userHasEmailCampaignReportAccess = () =>
  userHasEmailCampaignReportAccessRbac() || userHasEmailCampaignAccessLegacy()
export const userCanEditEmailCampaigns = () =>
  userCanEditEmailCampaignsRbac() || userHasEmailCampaignAccessLegacy()

export const isSalesManager = () => hasAnyDefaultRole('Sales Manager')

export const hasFullRoutedChatAccess = () =>
  hasGates('has_full_routed_chat_access')

export const hasRevenueReportAccess = () =>
  userHasAllReportAccess() && userHasSalesResultsReportsFeature()

export const hasVisualOppotunityTimeline = () =>
  hasRevenueReportAccess() && hasGates('roi_v3')

export const userHasAIAnswersSkill = () => hasGates('has_bionic')

// Site Concierge
export const userHasContentRecommendations = () =>
  hasGates('content_recommendations')

export const userHasSearchAppExperience = () =>
  hasGates('search_app_experience')

export const userHasMeetingBookingAppExperience = () =>
  hasGates('meeting_booking_experience')

export const scWindDown = () => hasGates('sc_wind_down_dev')
export const onlySearch = () => scWindDown() && userHasSearchAppExperience()

export const userHasSiteConciergeAccess = () =>
  (userHasContentRecommendations() ||
    userHasSearchAppExperience() ||
    userHasMeetingBookingAppExperience()) &&
  !onlySearch()

export const hasIntelligentSearchFeedbackLoop = () => true

export const hasAIConversationInsights = () => hasGates('ai_convo_insights')
